import { Button } from "@/design-system/Button/Button";
import { EvolutionUpgradePlayerCard } from "@/design-system/PlayerCard/PlayerCard";
import { openLinkIcon } from "@/designSystemIcons";
import {
  EvolutionsSubsection,
  trackExplore,
  trackViewContent,
} from "@/events/evolutionsMixpanel";
import {
  Evolution,
  setIncludedEvolutions,
  setMustIncludeEvolutions,
} from "@/redux/evolutions/evolutionsSlice";
import {
  clearFilter,
  generateNavigationUrl,
  updateFilter,
} from "@/redux/players/playersSlice";
import { AppDispatch } from "@/redux/store";
import { BREAKPOINTS } from "@/settings";
import { useTranslation } from "@repo/shared/i18n";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import { useMediaQuery } from "usehooks-ts";

/**
 * The "Recommended" section of an evolution: its top players as before/after
 * tiles, plus the two ways into the players list filtered to it.
 */
export const EvoRecommended = ({
  evolution,
  subsection,
}: {
  evolution: Evolution;
  /** Where this renders, for tracking. */
  subsection: EvolutionsSubsection;
}) => {
  const { t } = useTranslation("evolutions");
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  const topPlayers = evolution.topPlayers.slice(0, isMobile ? 1 : 3);

  const goToPlayers = () =>
    navigate("/evolutions/players", { state: { fromDetailedEvoPage: true } });

  const handleCardClick = () => {
    trackViewContent({
      subsection,
      content_type: "evo_player",
      action_type: "browse",
      new_content: evolution.newEvolution,
    });
  };

  const handleMorePlayers = () => {
    trackExplore({
      subsection,
      source: "more players",
      destination_section: "Evolutions",
      destination_subsection: "Players",
      new_content: evolution.newEvolution,
    });
    const chainLength =
      evolution.topPlayers[0]?.evolutionPlayer.evolutionPath?.length ?? 0;
    const includedEvolutionIds = [
      ...new Set(
        evolution.topPlayers.flatMap(
          (topPlayer) => topPlayer.evolutionPlayer.evolutionPath ?? [],
        ),
      ),
    ];

    dispatch(setMustIncludeEvolutions([evolution.id]));
    if (chainLength > 1) {
      dispatch(
        setIncludedEvolutions(
          includedEvolutionIds.length > 0
            ? includedEvolutionIds
            : [evolution.id],
        ),
      );
      dispatch(updateFilter({ key: "max-chain-length", max: chainLength }));
    } else {
      dispatch(setIncludedEvolutions([evolution.id]));
      dispatch(clearFilter("max-chain-length"));
    }
    goToPlayers();
  };

  const handleChains = () => {
    trackExplore({
      subsection,
      source: "chains with this evolution",
      destination_section: "Evolutions",
      destination_subsection: "Players",
      new_content: evolution.newEvolution,
    });
    dispatch(setMustIncludeEvolutions([evolution.id]));
    goToPlayers();
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {topPlayers.length > 0 && (
          <h3 className="text-sm font-semibold text-foreground">
            {t("detail.recommended")}
          </h3>
        )}
        <div className="ml-auto flex flex-wrap gap-2">
          {topPlayers.length > 0 && (
            <Button
              variant="secondary"
              size="small"
              onClick={handleMorePlayers}
              icon={<div className="size-3">{openLinkIcon}</div>}
              iconPosition="right"
            >
              {t("detail.morePlayers")}
            </Button>
          )}
          <Button
            variant="secondary"
            size="small"
            onClick={handleChains}
            icon={<div className="size-3">{openLinkIcon}</div>}
            iconPosition="right"
          >
            {t("detail.chainsWithThisEvolution")}
          </Button>
        </div>
      </div>
      {topPlayers.length > 0 && (
        <div className="grid grid-cols-1 gap-3 mobile:grid-cols-2 touch-device:grid-cols-3">
          {topPlayers.map((topPlayer) => (
            <EvolutionUpgradePlayerCard
              key={topPlayer.evolutionPlayer.resourceId}
              basePlayer={topPlayer.basePlayer}
              evolutionPlayer={topPlayer.evolutionPlayer}
              href={generateNavigationUrl({
                resourceId: topPlayer.evolutionPlayer.resourceId,
                name: topPlayer.evolutionPlayer.name,
                evolutionPath: topPlayer.evolutionPlayer.evolutionPath,
                pathwayPath: topPlayer.evolutionPlayer.pathwayPath,
                basePath: "/evolutions/players",
              })}
              onClick={handleCardClick}
              showBasePlayerMetaRating
              hidePrice
            />
          ))}
        </div>
      )}
    </div>
  );
};
